/**
 * TemporaryIdService is a service that holds of the next ids for any element type.
 * By default ids start at -1 and are decremented by 1 on each getNextId call.
 * @packageDocumentation
 * @module TemporaryIdService
 */

import { Injectable } from "../dependencies/injectable";
import { setProp } from "../tools/any.tools";

export const TEMPORARY_ID_SERVICE = "TemporaryIdService";

export interface TemporaryIdService<ElementTypeName = string> {

    /**
     * @returns the next id for the elementTypeName and increments for next call
     */
    getNextId(elementTypeName: ElementTypeName): number;

}

/**
 * Default implementation of the TemporaryIdService
 */
@Injectable(TEMPORARY_ID_SERVICE)
export class DefaultTemporaryIdService<ElementTypeName = string> implements TemporaryIdService<ElementTypeName> {

    // This map hold the next temporary id for each type
    private _nextIds: Map<ElementTypeName, number> = new Map<ElementTypeName, number>();

    /**
     * @param _increment by default, ids start at -1 and are decremented by 1 on each call, you can change this value to 1 to have positive numbers
     */
    constructor(private _increment: number = -1) { }

    /**
     * @returns the next id for the elementTypeName and increments for next call
     */
    public getNextId(elementTypeName: ElementTypeName): number {
        let nextId: number | undefined = this._nextIds.get(elementTypeName);
        if (!nextId) {
            nextId = this._increment;
        }
        this._nextIds.set(elementTypeName, nextId + this._increment);
        return nextId;
    }


    /**
     * Overrides the next id for elementTypeName in the internal map only if provided id is below the current stored value
     */
    public overrideNextIdIfLessThanCurrent(elementTypeName: ElementTypeName, id: number): void {
        const nextId: number | undefined = this._nextIds.get(elementTypeName);
        if (!nextId || id < nextId) {
            this._nextIds.set(elementTypeName, id);
        }
    }

    /**
     * Resets _nextIds map, all newly created objects will get ids starting from -1
     */
    public resetNextIds(): void {
        this._nextIds = new Map<ElementTypeName, number>();
    }

    /**
     * This method gets the internal map as json.
     * It is used to store the next ids in indexed db for example, nextIds can then be restored using overrideNextIdsMap
     */
    public getNextIdsJsonMap(): { [elementTypeName: string]: number } {
        const jsonMap = {};
        for (const [key, value] of this._nextIds) {
            setProp(jsonMap, String(key), value);
        }
        return jsonMap;
    }

    /**
     * This method overrides the nextIds in the internal map.
     * It is used to restore the next ids from indexed db for example
     */
    public overrideNextIdsMap(nextIds: { [elementTypeName: string]: number }): void {
        if (nextIds != null) {
            for (const elementTypeName in nextIds) {
                setProp(this._nextIds, elementTypeName, nextIds[elementTypeName]);
            }
        }
    }

}